import { html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './search.css.lit.ts';
import { OutlineElement } from '../../base/outline-element/outline-element.ts';
import { debounce } from '../../utils/debounce';

export default class Search extends OutlineElement {
  static styles = [super.styles, componentStyles];

  static get properties() {
    return {
      placeholder: { type: String },
      searchUrl: { type: String },
      type: { type: String },
      physicians: { type: Object },
      services: { type: Object },
      locations: { type: Object },
      fullSearch: { type: Object },
      isHPSite: { type: Boolean },
      mainMenuSearch: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.searchValue = '';
    this.minSearchChars = 4;
    this.abortController = null;
    this.panelOpen = false;
    this.showClearButton = this.searchValue.length > 0;
    this.physicians = {
      items: [],
      viewMoreUrl: 'https://providers.hf.org/search?unified=',
    };
    this.services = {
      items: [],
      viewMoreUrl: '/search?f[0]=content_type:departments_services&search=',
    };
    this.locations = {
      items: [],
      viewMoreUrl: '/search?f[0]=content_type:location&search=',
    };
    this.fullSearch = {
      items: [],
      viewMoreUrl: '/search?search=',
    };
    this.lineDesign = false;
    this.isHPSite = false;
    this.mainMenuSearch = false;
  }

  // @TODO: refactor as something that we can import into other components
  doQuery(url) {
    // cancel pending request if any
    if (this.abortController) this.abortController.abort();
    // make our request cancellable
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    return (
      fetch(url, { method: 'post', signal })
        // clean up, not really needed but may help to know if there is a pending request
        .then(r => {
          this.abortController = null;
          return r;
        })
    );
  }

  _handleChangeDebounced(e) {
    debounce(this._handleChange(e));
  }

  _handleChange(e) {
    this.showClearButton = e.target.value.length > 0;
    this.searchValue = e.target.value;

    // if (
    //   this.searchUrl.length &&
    //   this.searchValue.length >= this.minSearchChars
    // ) {
    //   this.panelOpen = true;
    //   this._handleSearch();
    // } else {
    //   this.panelOpen = false;
    //   this.requestUpdate();
    // }

    // Enter key
    if (e.keyCode === 13) {
      window.location = this.searchUrl + this.searchValue;
    }

    // Escape key
    if (e.keyCode === 27) {
      this.panelOpen = false;
    }
  }

  firstUpdated() {
    this.abortController = new AbortController();
  }

  _handleData(data) {
    this.physicians.items = data.physicians;
    this.services.items = data.departments_and_services;
    this.locations.items = data.locations_search;
    this.fullSearch.items = data.full_search;
    this.requestUpdate();
  }

  _handleSubmit() {
    window.location = this.searchUrl + this.searchValue;
  }

  _handleSearch() {
    const searchValue = this.searchValue.trim();
    const url = '/search.ajax/' + searchValue + '?search=' + searchValue;
    this.doQuery(url)
      .then(response => response.json())
      .then(data => this._handleData(data))
      .catch(() => {});
  }

  _focusIn() {
    if (
      this.searchUrl.length &&
      this.searchValue.length >= this.minSearchChars
    ) {
      this.panelOpen = true;
    }
    this.requestUpdate();
  }

  _focusOut(e) {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
      this.panelOpen = false;
      this.requestUpdate();
    }
  }

  _handleClear() {
    this.searchValue = '';
    this.showClearButton = false;
    this.panelOpen = false;
    this.requestUpdate();
  }

  _handleOverlay() {
    this.panelOpen = false;
    this.requestUpdate();
  }

  renderOffleft(array) {
    const matchTxt = array.length <= 1 ? 'match found' : 'matches found';
    return html`<span class="search__offleft" tabindex="-1">
      ${array.length} ${matchTxt}
    </span>`;
  }

  render() {
    return html`<div
      class="${classMap({
        'search__container': true,
        [`search__${this.type}`]: !!this.type,
        'search__container--hp': this.isHPSite,
      })}"
      @focusout="${this._focusOut}"
    >
      <label for="globalSearch" class="search__offleft"
        >Search by department, service, procedure, condition or treatment</label
      >
      <input
        aria-label="${Drupal.t('search')}"
        name="globalSearch"
        id="globalSearch"
        class="search__input"
        type="text"
        .value=${this.searchValue}
        placeholder="${this.placeholder}"
        @keyup="${this._handleChangeDebounced}"
      />
      ${this.renderOffleft(this.fullSearch.items)}
      <button
        @click="${this._handleClear}"
        class="${classMap({
          'search__input-clear': true,
          'search__input-clear--show': this.showClearButton,
        })}"
        aria-label="Clear Search Field"
      >
        <hf-icon
          name="close"
          color="${this.isHPSite ? 'white' : 'purple'}"
        ></hf-icon>
      </button>

      <button
        @click="${this._handleSubmit}"
        type="submit"
        class="search__submit"
        aria-label="Search submit"
      >
        <hf-icon class="search__icon" name="search"></hf-icon>
      </button>
      <div
        @click="${this._handleOverlay}"
        class="${classMap({
          'search__overlay': true,
          'search__overlay--show':
            this.searchValue.length >= this.minSearchChars && this.panelOpen,
        })}"
      ></div>
      <hf-search-panel
        .panelIsOpen="${this.panelOpen}"
        .searchValue="${this.searchValue}"
        .physicians="${this.physicians}"
        .services="${this.services}"
        .locations="${this.locations}"
        .fullSearch="${this.fullSearch}"
        .mainMenuSearch="${this.mainMenuSearch}"
      ></hf-search-panel>
    </div>`;
  }
}
customElements.define('hf-search', Search);
